import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTrashAlt, FaUndoAlt, FaEye, FaTimes, FaKey } from 'react-icons/fa';
import { toRelativePath } from '../../utils/videoImagePath';
import { useUserManagement } from './useUserManagement';
import { UserManagementProps, UserManagementComponent } from './types';
import LoadingIndicator from '../../components/LoadingIndicator';
import styles from './UserManagement.module.css';

const UserManagement: UserManagementComponent = ({ currentUser }) => {
  const {
    filteredUsers,
    loading,
    error,
    searchTerm,
    showDeletedUsers,
    roleFilter,
    deleteConfirmUser,
    restoreConfirmUser,
    resetPasswordTargetUser,
    resetPasswordResult,
    showCreateUserModal,
    createUserEmail,
    createUserName,
    createUserResult,
    isCreatingUser,
    isSuccess,
    successMessage,
    currentHospitalName,
    setSearchTerm,
    setShowDeletedUsers,
    setRoleFilter,
    setDeleteConfirmUser,
    setRestoreConfirmUser,
    setResetPasswordTargetUser,
    setShowCreateUserModal,
    setCreateUserEmail,
    setCreateUserName,
    handleCreateUser,
    handleDeleteUser,
    handleRestoreUser,
    handleRoleChange,
    handleResetUserPassword,
    formatDate
  } = useUserManagement(currentUser);
  
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={styles['user-management-loading-container']}>
        <LoadingIndicator
          className={styles['user-management-loading-indicator']}
          message="データを読み込み中..."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['user-management-container']}>
        <div className={styles['user-management-header']}>
          <h1 className={styles['user-management-title']}>ユーザー管理</h1>
        </div>
        <div className={styles['user-management-error-container']}>
          <p className={styles['user-management-error-message']}>{error}</p>
          <button 
            className={styles['user-management-retry-button']}
            onClick={() => window.location.reload()}
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['user-management-container']} mobile-page-content`}>
      <div className={styles['user-management-header']}>
        <h1 className={styles['user-management-title']}>ユーザー管理</h1>
        {currentUser.hospital_id && (
          <div className={styles['user-management-hospital-name-display']}>
            {currentHospitalName}の管理
          </div>
        )}
      </div>
      
      {/* 成功メッセージ */}
      {isSuccess && (
        <div className={styles['user-management-success-message']}>
          {successMessage}
        </div>
      )}
      
      <div className={styles['user-management-content']}>
        {/* 左サイドバー：フィルターとカウント */}
        <div className={styles['user-management-sidebar']}>
          <div className={styles['user-management-filter-section']}>
            <h3>フィルター</h3>
            
            <div className={styles['user-management-filter-group']}>
              <label className={styles['user-management-filter-label']}>ステータス</label>
              <div className={styles['user-management-checkbox-container']}>
                <input
                  type="checkbox"
                  id="show-deleted"
                  checked={showDeletedUsers}
                  onChange={() => setShowDeletedUsers(!showDeletedUsers)}
                />
                <label htmlFor="show-deleted">削除済みユーザーを表示</label>
              </div>
            </div>

            <div className={styles['user-management-filter-group']}>
              <label className={styles['user-management-filter-label']}>ロール</label>
              <select 
                className={styles['user-management-filter-select']}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">すべて</option>
                <option value="admin">管理者</option>
                <option value="user">ユーザー</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* メインコンテンツ：ユーザーテーブル */}
        <div className={styles['user-management-main-content']}>
          {/* 検索バー */}
          <div className={styles['user-management-search-container']}>
            <div className={styles['user-management-search-bar']}>
              <FaSearch className={styles['user-management-search-icon']} />
              <input
                type="text"
                placeholder="ユーザーを検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles['user-management-search-input']}
                data-testid="user-management__search-input"
              />
              {searchTerm && (
                <button 
                  className={styles['user-management-clear-search']}
                  onClick={() => setSearchTerm('')}
                  data-testid="user-management__clear-search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles['user-management-count']}>
              全 {filteredUsers.length} 人表示
            </div>

            <button
              className={styles['user-management-create-user-btn']}
              onClick={() => setShowCreateUserModal(true)}
              data-testid="user-management__create-user-btn"
            >
              ＋ ユーザー追加
            </button>
          </div>
          
          {/* ユーザーリスト */}
          <div className={styles['user-management-grid']}>
            {filteredUsers.length === 0 ? (
              <div className={styles['user-management-no-results']}>
                該当するユーザーが見つかりません
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`${styles['user-management-card']} ${user.deleted_at ? styles['user-management-deleted-card'] : ''}`}
                  data-testid={`user-management__card-${user.id}`}
                >
                  <div className={styles['user-management-card-header']}>
                    <div className={styles['user-management-avatar']}>
                      {user.icon_path ? (
                        <img 
                          src={toRelativePath(user.icon_path)}
                          alt={`${user.name}のアイコン`}
                        />
                      ) : (
                        <div className={styles['user-management-avatar-placeholder']}>
                          {user.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className={styles['user-management-info']}>
                      <h3 className={styles['user-management-name']}>{user.name}</h3>
                      <p className={styles['user-management-email']}>{user.email}</p>
                    </div>
                  </div>
                  
                  <div className={styles['user-management-card-body']}>
                    <div className={styles['user-management-detail']}>
                      <span className={styles['user-management-detail-label']}>権限</span>
                      <select 
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value as 'admin' | 'user')}
                        disabled={user.id === currentUser.id || !!user.deleted_at}
                        className={styles['user-management-role-select']}
                        data-testid={`user-management__role-select-${user.id}`}
                      >
                        <option value="user">ユーザー</option>
                        <option value="admin">管理者</option>
                      </select>
                    </div>
                    
                    <div className={styles['user-management-detail']}>
                      <span className={styles['user-management-detail-label']}>状態</span>
                      <span className={user.deleted_at 
                        ? styles['user-management-deleted-status'] 
                        : styles['user-management-active-status']}
                      >
                        {user.deleted_at ? '削除済み' : '有効'}
                      </span>
                    </div>
                    
                    {user.deleted_at && (
                      <div className={styles['user-management-detail']}>
                        <span className={styles['user-management-detail-label']}>削除日時</span>
                        <span>{formatDate(user.deleted_at)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className={styles['user-management-card-footer']}>
                    <button 
                      className={`${styles['user-management-card-btn']} ${styles['user-management-view-btn']}`}
                      onClick={() => navigate(`/users/${user.id}`)}
                      title="詳細を表示"
                      data-testid={`user-management__view-btn-${user.id}`}
                    >
                      <FaEye />
                    </button>
                    
                    {user.id !== currentUser.id && !user.deleted_at && (
                      <>
                        <button
                          className={`${styles['user-management-card-btn']} ${styles['user-management-reset-password-btn']}`}
                          onClick={() => setResetPasswordTargetUser(user)}
                          title="パスワードをリセット"
                          data-testid={`user-management__reset-password-btn-${user.id}`}
                        >
                          <FaKey />
                        </button>
                        <button
                          className={`${styles['user-management-card-btn']} ${styles['user-management-delete-btn']}`}
                          onClick={() => setDeleteConfirmUser(user)}
                          title="ユーザーを削除"
                          data-testid={`user-management__delete-btn-${user.id}`}
                        >
                          <FaTrashAlt />
                        </button>
                      </>
                    )}
                    
                    {user.deleted_at && (
                      <button 
                        className={`${styles['user-management-card-btn']} ${styles['user-management-restore-btn']}`}
                        onClick={() => setRestoreConfirmUser(user)}
                        title="ユーザーを復元"
                        data-testid={`user-management__restore-btn-${user.id}`}
                      >
                        <FaUndoAlt />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* 削除確認モーダル */}
      {deleteConfirmUser && (
        <div className={styles['user-management-modal-backdrop']}>
          <div className={styles['user-management-confirm-modal']}>
            <h3>ユーザーを削除</h3>
            <p>
              <strong>{deleteConfirmUser.name}</strong> を削除してもよろしいですか？<br />
              このユーザーは論理削除され、後から復元することができます。
            </p>
            <div className={styles['user-management-modal-actions']}>
              <button 
                className={styles['user-management-cancel-btn']}
                onClick={() => setDeleteConfirmUser(null)}
                data-testid="user-management__cancel-delete-btn"
              >
                キャンセル
              </button>
              <button 
                className={styles['user-management-delete-btn']}
                onClick={() => handleDeleteUser(deleteConfirmUser.id)}
                data-testid="user-management__confirm-delete-btn"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 復元確認モーダル */}
      {restoreConfirmUser && (
        <div className={styles['user-management-modal-backdrop']}>
          <div className={styles['user-management-confirm-modal']}>
            <h3>ユーザーを復元</h3>
            <p>
              <strong>{restoreConfirmUser.name}</strong> を復元してもよろしいですか？<br />
              このユーザーは再び有効になります。
            </p>
            <div className={styles['user-management-modal-actions']}>
              <button
                className={styles['user-management-cancel-btn']}
                onClick={() => setRestoreConfirmUser(null)}
                data-testid="user-management__cancel-restore-btn"
              >
                キャンセル
              </button>
              <button
                className={styles['user-management-restore-btn']}
                onClick={() => handleRestoreUser(restoreConfirmUser.id)}
                data-testid="user-management__confirm-restore-btn"
              >
                復元する
              </button>
            </div>
          </div>
        </div>
      )}

      {/* パスワードリセットモーダル */}
      {resetPasswordTargetUser && (
        <div className={styles['user-management-modal-backdrop']}>
          <div className={styles['user-management-confirm-modal']}>
            {resetPasswordResult === null ? (
              <>
                <h3>パスワードをリセット</h3>
                <p>
                  <strong>{resetPasswordTargetUser.name}</strong> の一時パスワードを発行しますか？<br />
                  ユーザーは次回ログイン時にパスワードの変更が必要になります。
                </p>
                <div className={styles['user-management-modal-actions']}>
                  <button
                    className={styles['user-management-cancel-btn']}
                    onClick={() => setResetPasswordTargetUser(null)}
                    data-testid="user-management__cancel-reset-password-btn"
                  >
                    キャンセル
                  </button>
                  <button
                    className={styles['user-management-restore-btn']}
                    onClick={() => handleResetUserPassword(resetPasswordTargetUser.id)}
                    data-testid="user-management__confirm-reset-password-btn"
                  >
                    リセットする
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>一時パスワードが発行されました</h3>
                <p>
                  以下の一時パスワードをユーザーに安全な方法で伝えてください。<br />
                  このダイアログを閉じると二度と表示されません。
                </p>
                <div
                  className={styles['user-management-temp-password-box']}
                  data-testid="user-management__temp-password"
                >
                  {resetPasswordResult}
                </div>
                <div className={styles['user-management-modal-actions']}>
                  <button
                    className={styles['user-management-cancel-btn']}
                    onClick={() => setResetPasswordTargetUser(null)}
                    data-testid="user-management__close-reset-password-btn"
                  >
                    閉じる
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ユーザー作成モーダル */}
      {showCreateUserModal && (
        <div className={styles['user-management-modal-backdrop']}>
          <div className={styles['user-management-confirm-modal']}>
            {createUserResult === null ? (
              <>
                <h3>ユーザーを追加</h3>
                <p>メールアドレスと名前を入力してください。<br />一時パスワードが発行されます。</p>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                    メールアドレス <span style={{ color: '#f5222d' }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={createUserEmail}
                    onChange={(e) => setCreateUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                    data-testid="user-management__create-email-input"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#666' }}>
                    名前（任意）
                  </label>
                  <input
                    type="text"
                    value={createUserName}
                    onChange={(e) => setCreateUserName(e.target.value)}
                    placeholder="山田 太郎"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                    data-testid="user-management__create-name-input"
                  />
                </div>
                <div className={styles['user-management-modal-actions']}>
                  <button
                    className={styles['user-management-cancel-btn']}
                    onClick={() => setShowCreateUserModal(false)}
                    data-testid="user-management__cancel-create-user-btn"
                  >
                    キャンセル
                  </button>
                  <button
                    className={styles['user-management-restore-btn']}
                    onClick={handleCreateUser}
                    disabled={!createUserEmail.trim() || isCreatingUser}
                    data-testid="user-management__confirm-create-user-btn"
                  >
                    {isCreatingUser ? '作成中...' : '作成する'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>ユーザーが作成されました</h3>
                <p>
                  以下の一時パスワードをユーザーに安全な方法で伝えてください。<br />
                  このダイアログを閉じると二度と表示されません。
                </p>
                <div
                  className={styles['user-management-temp-password-box']}
                  data-testid="user-management__create-user-temp-password"
                >
                  {createUserResult}
                </div>
                <div className={styles['user-management-modal-actions']}>
                  <button
                    className={styles['user-management-cancel-btn']}
                    onClick={() => setShowCreateUserModal(false)}
                    data-testid="user-management__close-create-user-btn"
                  >
                    閉じる
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 
